import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Group,
  Paper,
  MultiSelect,
  Select,
  TextInput,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import {
  MessageState,
  MESSAGE_STATE_VALUES,
  MessageCategoryConfig,
} from '#/components/ui/messages/model';
import { MESSAGE_STATE_CONFIG } from '../components/MessageStatusBadge';
import type { MessageTemplatesFilterParams } from '#/components/ui/messages/endpoints/messageTemplateList';
import {
  MessageTemplatesFilterSchema,
  useMessageCategories,
} from '../endpoints';

const MessageTemplateSearchBarParams = {
  STATE: 'state',
  TITLE: 'title',
  CATEGORY: 'category',
} as const;

type SearchParams = {
  [MessageTemplateSearchBarParams.STATE]: MessageTemplatesFilterParams['state'];
  [MessageTemplateSearchBarParams.TITLE]:
    | MessageTemplatesFilterParams['title']
    | undefined;
  [MessageTemplateSearchBarParams.CATEGORY]:
    | MessageTemplatesFilterParams['category']
    | undefined;
};

type SearchParamsKeys =
  (typeof MessageTemplateSearchBarParams)[keyof typeof MessageTemplateSearchBarParams];

const stateSchema = MessageTemplatesFilterSchema.shape.state;

export type DefaultOptionsParams = {
  state?: MessageState[];
  title?: string;
  category?: string;
};

export function useMessageTemplateSearchBarParams(
  defaultOptions?: DefaultOptionsParams,
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawState = searchParams.getAll(MessageTemplateSearchBarParams.STATE);
  const title =
    searchParams.get(MessageTemplateSearchBarParams.TITLE) ?? undefined;
  const category =
    searchParams.get(MessageTemplateSearchBarParams.CATEGORY) ?? undefined;
  const state = stateSchema.parse(rawState);

  useEffect(() => {
    const defaultInit = defaultOptions
      ? (() => {
          const sp = new URLSearchParams();
          if (defaultOptions?.title)
            sp.set(MessageTemplateSearchBarParams.TITLE, defaultOptions.title);
          if (defaultOptions?.state) {
            for (const s of defaultOptions.state) {
              sp.append(MessageTemplateSearchBarParams.STATE, s);
            }
          }
          if (defaultOptions?.category)
            sp.set(
              MessageTemplateSearchBarParams.CATEGORY,
              defaultOptions.category,
            );
          return sp;
        })()
      : undefined;

    if (defaultInit) {
      setSearchParams(defaultInit);
    }
    // only apply first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSearch = <T extends SearchParamsKeys>(
    key: T,
    value: SearchParams[T],
  ) => {
    setSearchParams((prev) => {
      if (value == null || value === '') {
        prev.delete(key);
      } else if (Array.isArray(value)) {
        prev.delete(key);
        for (const v of value) {
          prev.append(key, v);
        }
      } else {
        prev.set(key, value as string);
      }
      return prev;
    });
  };

  const createSearchSetter =
    <T extends SearchParamsKeys>(key: T) =>
    (value: SearchParams[T]) =>
      setSearch(key, value);

  const setState = createSearchSetter(MessageTemplateSearchBarParams.STATE);
  const setTitle = createSearchSetter(MessageTemplateSearchBarParams.TITLE);
  const setCategory = createSearchSetter(
    MessageTemplateSearchBarParams.CATEGORY,
  );

  const params: SearchParams = { state, title, category };

  const reset = () => {
    setSearchParams((prev) => {
      prev.delete(MessageTemplateSearchBarParams.STATE);
      prev.delete(MessageTemplateSearchBarParams.TITLE);
      prev.delete(MessageTemplateSearchBarParams.CATEGORY);
      return prev;
    });
  };

  return {
    params,
    setState,
    setTitle,
    setCategory,
    setSearchParams,
    reset,
  };
}

const getStatesOption = (availStates: MessageState[] = MESSAGE_STATE_VALUES) =>
  availStates.map((s) => ({
    value: s,
    label: MESSAGE_STATE_CONFIG[s].label,
  }));

const SelectCategoryOptions = ({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
}) => {
  const { data = [] as MessageCategoryConfig[], isLoading } =
    useMessageCategories();

  const options = data.map(({ key, name }) => ({ value: key, label: name }));

  return (
    <Select
      w={160}
      data={options}
      loading={isLoading}
      placeholder="全部類別"
      clearable
      value={value}
      onChange={onChange}
    />
  );
};

export type AvailMessageTemplateSearchBarField = {
  state?: boolean;
  category?: boolean;
  search?: boolean;
};

export type MessageTemplateSearchBarProps = {
  defaultOptions?: DefaultOptionsParams;
  availField?: AvailMessageTemplateSearchBarField;
  availStates?: MessageState[];
};

export function MessageTemplateSearchBar({
  defaultOptions,
  availField,
  availStates,
}: MessageTemplateSearchBarProps) {
  const { params, setState, setTitle, setCategory, reset } =
    useMessageTemplateSearchBarParams(defaultOptions);
  const [inputTitleValue, setInputTitleValue] = useState(
    () => params.title ?? '',
  );

  const statesOptions = getStatesOption(availStates);

  return (
    <Paper withBorder p="sm">
      <Group gap="sm">
        {availField?.search !== false && (
          <TextInput
            flex={1}
            value={inputTitleValue}
            onChange={(e) => {
              setTitle(e.target.value);
              setInputTitleValue(e.target.value);
            }}
            type="text"
            placeholder="搜尋標題"
            leftSection={<IconSearch size={16} />}
          />
        )}
        {availField?.state !== false && (
          <MultiSelect
            w={160}
            data={statesOptions}
            placeholder={params.state.length < 1 ? '全部狀態' : undefined}
            clearable
            value={params.state}
            renderPill={({ option }) => {
              if (option.value !== params.state[0]) {
                return null;
              }
              if (params.state.length > 1) {
                return '...';
              }
              return option.label;
            }}
            onChange={(v) => setState(v as MessageState[])}
          />
        )}
        {availField?.category !== false && (
          <SelectCategoryOptions
            value={params.category ?? null}
            onChange={(v) => setCategory(v ?? undefined)}
          />
        )}
        <Button variant="default" onClick={reset}>
          重設
        </Button>
      </Group>
    </Paper>
  );
}
